import React, { useState } from "react";

import { Button } from "../../../common/u1/Button";
import { Modal } from "../../../common/Modal";
import { CommonToast } from "../../../common/Toast";
import {
  UsersIcon,
  BuildingIcon,
  MapPinIcon,
  AlertIcon,
} from "../../../../assets/ui/icons";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { SectionAddBar } from "./SectionAddBar";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { SectionNotice } from "./SectionNotice";
import { MembershipVerifyNotice } from "./MembershipVerifyNotice";
import { DetailCard } from "./DetailCard";
import { EditDialog } from "./EditDialog";
import { OwnershipFormModal } from "./OwnershipFormModal";
import { OwnershipSearchModal } from "./OwnershipSearchModal";
import { OwnershipAddCompanyModal } from "./OwnershipAddCompanyModal";
import { VerificationModal } from "./VerificationModal";
import { useOwnershipStructure } from "./useOwnershipStructure";
import {
  OWNERSHIP_ROWS,
  canContinue,
  canEditOwner,
  isCompanyOwner,
  ownershipKey,
  ownershipRowValues,
  ownershipStatus,
} from "./ownershipFields";

/* Section 08 — Ownership Structure. */

const ROW_ICONS = {
  type: UsersIcon,
  name: UsersIcon,
  country: MapPinIcon,
  percentage_of_shares_held: BuildingIcon,
};

/* Ownership documents share the company's own document path — there is no ownership-specific one in uploadFilePath. */
const VERIFY_SPEC = {
  uploadPath: uploadFilePath?.authLetters,
  category: "supporting_document",
};

export const OwnershipStructureSection = ({
  onNext,
  onPrev,
  onSkip,
  header,
  className = "",
}) => {
  const { rows, usingDrafts, total, remove, refresh, companyStatus } =
    useOwnershipStructure();

  /* Adding a corporate owner is three steps in one dialog, as the live flow
     is: the form, the business search, and naming a business the search cannot
     find. `step` is null when nothing is open. */
  const [step, setStep] = useState(null);
  const [editing, setEditing] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  /* A company chosen from the business search, held while the dialog is open. */
  const [selectedCompany, setSelectedCompany] = useState(null);

  const remaining = parseFloat((100 - total).toFixed(2));

  /* Transcribed: at 100% the Add control refuses rather than opening a dialog
     that cannot be saved. The live version used alert(). */
  const openAdd = () => {
    if (total === 100) {
      CommonToast.error(
        "The total percentage of shares held is 100%. Try to edit the % before adding new"
      );
      return;
    }
    setEditing(null);
    setSelectedCompany(null);
    setStep("form");
  };

  const openEdit = (record) => {
    setEditing(record);
    setSelectedCompany(null);
    setStep("form");
  };

  const closeForm = () => {
    setStep(null);
    setEditing(null);
    setSelectedCompany(null);
  };

  /* Transcribed: Continue only moves at 0% or exactly 100%; in between it
     opens the "Action Required!" dialog. */
  const onContinue = () => {
    if (canContinue(total)) {
      onNext?.();
      return;
    }
    setIncompleteOpen(true);
  };

  const cardRows = (record) => {
    const values = ownershipRowValues(record);
    return OWNERSHIP_ROWS.map((row) => ({
      key: row.key,
      /* The second row is "Company name" for a corporate owner and "Name" for
         a person, as the design shows. */
      label: row.key === "name" ? values.nameLabel : row.label,
      icon: row.key === "name" && isCompanyOwner(record) ? BuildingIcon : ROW_ICONS[row.key],
      value: values[row.key],
    }));
  };

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        <EditFooterBar onBack={onPrev} onSkip={onSkip} onContinue={onContinue} />
      }
    >
      <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
        Ownership Structure
      </h2>

      {/* The live screen only mentioned the remaining share inside the form's
          field label, so there was no way to see it from here. */}
      <SectionNotice className="mt-3.5" tone="info">
        {remaining > 0 ? (
          <>
            <b className="font-bold">{remaining}% of shares remaining.</b> Add
            owners until the total reaches 100%. Each owner is an individual or a
            company you can search for.
          </>
        ) : (
          <>
            <b className="font-bold">All 100% of shares are allocated.</b> Edit
            an owner&apos;s percentage to make room for another.
          </>
        )}
      </SectionNotice>

      {/* Members only — see MembershipVerifyNotice. The offer says the
          verification is free WITH YOUR MEMBERSHIP, so it is not shown to
          anyone who does not have one. */}
      <MembershipVerifyNotice onUpload={() => setVerifyOpen(true)} />

      <SectionAddBar
        className="mt-4"
        prompt="Register a New Company Owner"
        label="Add"
        onAdd={openAdd}
      />

      {rows.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {rows.map((record) => (
            <DetailCard
              key={ownershipKey(record)}
              status={ownershipStatus(record)}
              rows={cardRows(record)}
              /* Transcribed: on a Verified company only an individual owner
                 stays editable among the draft rows. */
              onEdit={
                canEditOwner({ companyStatus, record, isDraftRow: usingDrafts })
                  ? () => openEdit(record)
                  : undefined
              }
              onDelete={() => remove(record)}
            />
          ))}
        </div>
      )}

      {step === "form" && (
        <Modal modalHandle={closeForm} noClose bare>
          <OwnershipFormModal
            editing={editing}
            total={total}
            selectedCompany={selectedCompany}
            onClose={closeForm}
            onSaved={() => {
              closeForm();
              refresh();
            }}
            onSearchCompany={() => setStep("search")}
          />
        </Modal>
      )}

      {step === "search" && (
        <Modal modalHandle={closeForm} noClose bare>
          <OwnershipSearchModal
            onClose={closeForm}
            /* The breadcrumb's first crumb goes back to the form, keeping
               whatever was already chosen. */
            onBack={() => setStep("form")}
            onSelect={(company) => {
              setSelectedCompany(company);
              setStep("form");
            }}
            onNotFound={() => setStep("addCompany")}
          />
        </Modal>
      )}

      {step === "addCompany" && (
        <Modal modalHandle={closeForm} noClose bare>
          <OwnershipAddCompanyModal
            onClose={closeForm}
            onBack={() => setStep("search")}
            /* A manually named business has no win and no companyID, so the
               ownership row is written with an empty ownershipCompany — the
               live behaviour. */
            onAdd={(company) => {
              setSelectedCompany(company);
              setStep("form");
            }}
          />
        </Modal>
      )}

      {verifyOpen && (
        <Modal modalHandle={() => setVerifyOpen(false)} noClose bare>
          <VerificationModal
            spec={VERIFY_SPEC}
            onClose={() => setVerifyOpen(false)}
            onDone={() => {
              setVerifyOpen(false);
              refresh();
            }}
          />
        </Modal>
      )}

      {/* Transcribed: the live "Action Required!" dialog, same two choices. */}
      {incompleteOpen && (
        <Modal modalHandle={() => setIncompleteOpen(false)} noClose bare>
          <EditDialog
            title="Action required"
            onClose={() => setIncompleteOpen(false)}
            width={420}
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-[2px] shrink-0 text-[var(--warning)]"
              >
                <AlertIcon size={18} />
              </span>
              <p className="m-0 text-[13px] leading-[1.55] text-[var(--ink-2)] font-sans">
                <b className="font-bold">Ownership does not add up to 100%.</b>{" "}
                It currently totals {total}%, leaving {remaining}% unallocated.
                The ownership chart needs a complete set before it can be
                produced.
              </p>
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIncompleteOpen(false);
                  onSkip?.();
                }}
              >
                Skip anyway
              </Button>
              <Button
                variant="coralSolid"
                onClick={() => {
                  setIncompleteOpen(false);
                  openAdd();
                }}
              >
                Add ownership
              </Button>
            </div>
          </EditDialog>
        </Modal>
      )}
    </EditSectionLayout>
  );
};

export default OwnershipStructureSection;
